import type { HelpArticle } from "../../types";
import { MAPLE_PROJECT } from "../../../calculations/calcPreviewFixtures";
import { SCHEMA_SHEET_CAPTURE_FIXTURE } from "../../../calculations/helpCaptureFixtures";

export const schemaSheetsArticle: HelpArticle = {
  id: "schema-sheets",
  title: "Registry-driven calculation sheets",
  description:
    "Understand how calculator registry groups, fields, catalog bindings, and returned records become a calculation workspace.",
  tags: ["calculator registry", "schema", "catalog binding", "stable key", "result record"],
  lastUpdated: "July 20, 2026",
  published: false,
  blocks: [
    {
      kind: "lead",
      text: "A registry-driven sheet composes one shared workspace from calculator metadata. The registry declares groups, fields, units, defaults, limits, help, and catalog bindings; the selected calculator returns the result record. The portal renders those contracts instead of hand-building a form for every calculator.",
    },
    { kind: "where", path: ["Calculations", MAPLE_PROJECT.name, SCHEMA_SHEET_CAPTURE_FIXTURE.mark] },
    {
      kind: "callout",
      variant: "note",
      title: "What the figures prove",
      body: `The ${SCHEMA_SHEET_CAPTURE_FIXTURE.mark} figures use a ${SCHEMA_SHEET_CAPTURE_FIXTURE.kind} shared by this article and the dev capture harness. They demonstrate interface structure and lifecycle states; they are not an engine snapshot or a steel design verification example.`,
    },

    { kind: "heading", level: 2, text: "The registry owns the input structure", id: "registry-inputs" },
    {
      kind: "paragraph",
      text: "Each declared input group becomes an ordered card. A field's type chooses the control; its label, unit, default, minimum, maximum, options, and help remain attached to that control. The workspace does not infer missing units or silently add fields outside the selected calculator's schema.",
    },
    { kind: "registry-group", calculatorId: "steel_beam_aisc", groupId: "geometry" },
    { kind: "registry-field", calculatorId: "steel_beam_aisc", fieldKey: "unbraced_length_ft" },
    { kind: "registry-field", calculatorId: "steel_beam_aisc", fieldKey: "required_moment_kipft" },
    {
      kind: "shot",
      shotId: "schema-sheet-input-groups",
      caption: "The registry's Stability group renders as a shared input card with its declared field labels, units, defaults, and limits.",
    },
    {
      kind: "paragraph",
      text: "Number and text fields commit on blur or Enter. Select and boolean controls commit when changed. Until a typed value commits, the header says **Unsaved changes** and the prior successful record remains the saved result.",
    },

    { kind: "heading", level: 2, text: "Catalog bindings preserve stable identity", id: "catalog-reference" },
    {
      kind: "paragraph",
      text: "A catalog binding names the selector experience, catalog entity, stable key, readiness state, and any dependency. The sheet stores the stable key expected by the calculator and shows a readable sidecar label. Display resolution never turns a label into the calculation identity.",
    },
    {
      kind: "shot",
      shotId: "schema-sheet-database-selection",
      caption: "Section and grade selections keep readable records paired with the stable keys sent to the calculator.",
    },
    {
      kind: "callout",
      variant: "warning",
      title: "Reselect dependent records after changing an upstream binding",
      body: "The picker removes an incompatible dependent choice inside the open modal, but the current scoped apply merges only the returned patch. An omitted dependent key does not clear an already saved dependent key or its label sidecars. After changing the upstream selection, deliberately choose the dependent record again before relying on the calculation.",
    },

    { kind: "heading", level: 2, text: "A commit replaces inputs and result together", id: "commit" },
    {
      kind: "paragraph",
      text: "Committed schema edits enter a serialized compute queue. A later edit made during a request is queued behind it. The previous successful record stays visible while the replacement is running. Success updates the saved calculation and result together; failure adds **Latest recalculation failed** and keeps the previous record visible.",
    },
    {
      kind: "paragraph",
      text: "Leaving a field without changing its normalized value is quiet. **Recalculate** remains available to submit the current local values explicitly.",
    },

    { kind: "heading", level: 2, text: "The calculator owns the record", id: "result-record" },
    {
      kind: "paragraph",
      text: "The result pane renders the returned verdict, checks, demands, capacities, utilization, method, references, warnings, assumptions, limitations, sources, and trace when present. A blocked or incomplete calculator response stays visible as returned; the portal does not manufacture a pass or fail.",
    },
    {
      kind: "shot",
      shotId: "schema-sheet-result-record",
      caption: "A deterministic interface fixture exercising the returned-result layout; the figure is not a verified steel calculation.",
    },
    {
      kind: "paragraph",
      text: "Quick and Detailed change presentation density only. Lock disables editing and places a presentation banner over the same saved record. Neither action recalculates, approves, or changes provenance.",
    },

    { kind: "related", topics: ["sheet-lifecycle", "member-sheets", "design-method", "linking-reactions"] },
  ],
};
